import apiClient from '@u/apiClient'
import { isCanceledRequest } from '@c/addressBook/directoryRequest'
import FetchTaskDossierForDashboardController from '@/wayfinder/actions/App/Http/Controllers/TaskDossier/FetchTaskDossierForDashboardController'
import FetchTaskDossierGroupPageController from '@/wayfinder/actions/App/Http/Controllers/TaskDossier/FetchTaskDossierGroupPageController'
import GetTaskListController from '@/wayfinder/actions/App/Http/Controllers/Task/GetTaskListController'
import GetAllTaskDossierController from '@/wayfinder/actions/App/Http/Controllers/TaskDossier/GetAllTaskDossierController'
import GetTaskDossierByIdController from '@/wayfinder/actions/App/Http/Controllers/TaskDossier/GetTaskDossierByIdController'
import CreateTaskDossierController from '@/wayfinder/actions/App/Http/Controllers/TaskDossier/CreateTaskDossierController'
import UpdateTaskDossierDashboardController from '@/wayfinder/actions/App/Http/Controllers/UpdateTaskDossierDashboardController'
import GetNewModelController from '@/wayfinder/actions/App/Http/Controllers/NewModel/GetNewModelController'

const baseErrors = {}

function processSuccessCallback(payload) {
  if (payload.successCallback) {
    payload.successCallback()
  }
}

/**
 * The controller of the task listing request in flight, or null.
 *
 * Module state, not store state: it is not data, and committing it would put a
 * live AbortController into a reactive tree (same reasoning as
 * `contactDirectory/actions.ts`). Only one listing is ever wanted at a time.
 */
let listingRequestController: AbortController | null = null

/**
 * The controllers of the group page requests in flight. A set rather than one
 * slot: several groups may load their next page in parallel and must not
 * cancel each other.
 */
const groupPageRequestControllers = new Set<AbortController>()

/**
 * Abort the listing and every group page in flight, and forget their
 * controllers so a late answer fails the identity check.
 */
function abortAllTaskRequests() {
  listingRequestController?.abort()
  listingRequestController = null
  for (const controller of groupPageRequestControllers) {
    controller.abort()
  }
  groupPageRequestControllers.clear()
}

export default {
  // region fetch
  /**
   * The grouped task listing of a dashboard, superseding whatever was in flight.
   *
   * Every call aborts the previous listing and every group page still loading:
   * those pages extend a result the new listing is about to replace. A
   * superseded or cancelled call resolves null (not a failure: the interceptor
   * stays quiet about cancellations); a genuine failure still rejects.
   *
   * @param {object} _context
   * @param {object} payload the request body built by TaskContent
   * @returns {Promise<object|null>} the listing, or null when superseded or cancelled
   */
  fetchAllDossierTasks(_context, payload) {
    abortAllTaskRequests()

    const controller = new AbortController()

    listingRequestController = controller

    return apiClient
      .route(FetchTaskDossierForDashboardController.post(), payload, {
        signal: controller.signal
      })
      .then((response) => {
        // Aborting does not guarantee the response never arrives: a newer
        // listing (or a cancel) has taken over, so this answer is stale.
        if (listingRequestController !== controller) {
          return null
        }
        listingRequestController = null

        return response.data
      })
      .catch((reason) => {
        const superseded = listingRequestController !== controller
        if (!superseded) {
          listingRequestController = null
        }
        if (superseded || isCanceledRequest(reason)) {
          return null
        }

        throw reason
      })
  },
  /**
   * One more page of one group of the current listing.
   *
   * Several groups may load at once, so they do not abort each other; they are
   * all aborted by the next listing or by `cancelDossierTaskRequests`, and then
   * resolve null.
   *
   * @param {object} _context
   * @param {object} payload the listing request body plus group_key/page/per_page
   * @returns {Promise<object|null>} the page, or null when superseded or cancelled
   */
  fetchDossierTasksGroupPage(_context, payload) {
    const controller = new AbortController()

    groupPageRequestControllers.add(controller)

    return apiClient
      .route(FetchTaskDossierGroupPageController.post(), payload, {
        signal: controller.signal
      })
      .then((response) => {
        if (!groupPageRequestControllers.delete(controller)) {
          return null
        }

        return response.data
      })
      .catch((reason) => {
        const superseded = !groupPageRequestControllers.delete(controller)
        if (superseded || isCanceledRequest(reason)) {
          return null
        }

        throw reason
      })
  },
  /**
   * Call off the task listing and every group page in flight, without starting
   * another (board switch, page leaving). Safe to call when nothing is in flight.
   */
  cancelDossierTaskRequests() {
    abortAllTaskRequests()
  },
  async fetchTasksList({ commit }) {
    await apiClient
      .route(GetTaskListController.get())
      .then(async (response) => {
        // commit('setAddressList', response.data.addresses)
        // commit('setAddressMetadata', response.data.metadata)
      })
      .catch(async (reason) => {
        console.error('fetchTasksList', reason)
      })
  },
  fetchTaskDossierList({ commit }, payload) {
    return new Promise((resolve, reject) => {
      apiClient
        .route(GetAllTaskDossierController.get(), {
          params: { ['with-task-done']: payload }
        })
        .then((response) => {
          resolve(response.data)
        })
        .catch((reason) => {
          console.error('fetchTasksList', reason)
        })
    })
  },

  fetchNewTaskDossier: ({ commit }, payload) => {
    commit('auth/setErrors', baseErrors, { root: true })
    return new Promise((resolve, reject) => {
      apiClient
        .route(GetNewModelController.get({ query: { model: 'TaskDossier' } }), {
          params: payload.params
        })
        .then((response) => {
          resolve(response.data)
        })
        .catch((reason) => {
          reject(reason)
        })
    })
  },

  fetchTaskDossierById: ({ commit }, payload) => {
    commit('auth/setErrors', baseErrors, { root: true })
    return new Promise((resolve, reject) => {
      apiClient
        .route(GetTaskDossierByIdController.get({ task_dossier: payload.id }))
        .then((response) => {
          resolve(response.data)
        })
        .catch((reason) => {
          reject(reason)
        })
    })
  },
  // endregion

  // region store
  storeTaskDossier: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      apiClient
        .route(CreateTaskDossierController.post(), payload.task_dossier)
        .then((response) => {
          resolve(response.data)
        })
        .catch(async (reason) => {
          reject(reason)
        })
    })
  },
  // endregion

  setDataTransferObject({ commit }, payload) {
    commit('setDataTransferObject', payload)
  },

  // region update
  async updateTaskDashboard({ commit }, payload) {
    commit('auth/setErrors', baseErrors, { root: true })
    await apiClient
      .route(
        UpdateTaskDossierDashboardController.put({ task: payload.id }),
        payload.task
      )
      .then((response) => {
        if (response.status === 200) {
          processSuccessCallback(payload)
          commit('setTaskDossier', response.data.task)
          commit('dossier/setDossierInList', response.data.dossier, {
            root: true
          })
        }
      })
      .catch((reason) => {
        console.error('updateTask', reason)
      })
  }
  // endregion
}
